import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { CpuIcon, ServerIcon, RadioIcon, WifiIcon } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from 'recharts';
import { useSupabaseQuery } from '../hooks/useSupabaseQuery';
import { KPICard } from '../components/KPICard';
import { CardSkeleton, ChartSkeleton } from '../components/LoadingSkeleton';
/* ================= VENDOR ================= */
const getVendorName = mitra => {
  const m = mitra?.toLowerCase() || '';
  if (m.includes('huawei')) return 'Huawei';
  if (m.includes('zte')) return 'ZTE';
  if (m.includes('aviat')) return 'Aviat';
  if (m.includes('pst')) return 'PST';
  if (m.includes('eid')) return 'EID';
  return 'Other';
};
const VENDOR_COLORS = {
  Huawei: '#ef4444',
  ZTE: '#3b82f6',
  Aviat: '#22c55e',
  PST: '#9ca3af',
  EID: '#ffffff',
  Other: '#6b7280'
};
/* ================= TOOLTIP ================= */
const CustomTooltip = ({
  active,
  payload,
  label
}) => {
  if (active && payload && payload.length) {
    const filtered = payload.filter(p => p.value > 0);
    return <div style={{
      background: '#020617',
      border: '1px solid #334155',
      borderRadius: '10px',
      padding: '10px',
      color: '#e2e8f0'
    }} data-id="element-626" data-mp="7ouhfl9dyy34">
        <p style={{
        fontWeight: 'bold',
        marginBottom: 6
      }} data-id="element-627" data-mp="3u34a46kcms9p">
          {label}
        </p>

        {filtered.map((entry, i) => <p key={i} style={{
        color: entry.dataKey === 'EID' ? '#ffffff' : entry.color,
        fontWeight: 500
      }} data-id="element-628" data-mp="3l5twiek26r1m" data-mpi={i}>
            {entry.dataKey} : {entry.value}
          </p>)}
      </div>;
  }
  return null;
};
/* ================= HELPER ================= */
const addToMap = (map, type, vendor, value) => {
  if (!map.has(type)) {
    map.set(type, {});
  }
  const obj = map.get(type);
  obj[vendor] = (obj[vendor] || 0) + value;
};
const formatChart = map => Array.from(map, ([name, vendors]) => ({
  name,
  ...vendors
})).sort((a, b) => {
  const totalA = Object.values(a).reduce((s, v) => s + (v || 0), 0);
  const totalB = Object.values(b).reduce((s, v) => s + (v || 0), 0);
  return totalB - totalA;
}).slice(0, 8);
/* ================= COMPONENT ================= */
export function EquipmentAnalysis() {
  const {
    data = [],
    loading,
    error
  } = useSupabaseQuery({
    limit: 5000
  });
  const {
    kpis,
    chartData
  } = useMemo(() => {
    if (!data.length) return {
      kpis: null,
      chartData: null
    };
    let totalOduNe = 0;
    let totalOduFe = 0;
    let totalIduNe = 0;
    let totalIduFe = 0;
    let totalModemNe = 0;
    let totalModemFe = 0;
    let totalAntennaNe = 0;
    let totalAntennaFe = 0;
    const oduTypeMap = new Map();
    const iduTypeMap = new Map();
    const modemTypeMap = new Map();
    const antennaSizeMap = new Map();
    data.forEach(item => {
      const vendor = getVendorName(item.Mitra);
      const oduNe = Number(item.Jumlah_ODU_NE) || 0;
      const oduFe = Number(item.Jumlah_ODU_FE) || 0;
      const iduNe = Number(item.Volume_IDU_NE) || 0;
      const iduFe = Number(item.Volume_IDU_FE) || 0;
      const modemNe = Number(item.Volume_Modem_IDU_NE) || 0;
      const modemFe = Number(item.Volume_Modem_IDU_FE) || 0;
      const antennaNe = Number(item.Volume_Antenna_NE) || 0;
      const antennaFe = Number(item.Volume_Antenna_FE) || 0;
      totalOduNe += oduNe;
      totalOduFe += oduFe;
      totalIduNe += iduNe;
      totalIduFe += iduFe;
      totalModemNe += modemNe;
      totalModemFe += modemFe;
      totalAntennaNe += antennaNe;
      totalAntennaFe += antennaFe;
      if (item.Tipe_ODU_NE) addToMap(oduTypeMap, item.Tipe_ODU_NE, vendor, oduNe);
      if (item.Tipe_ODU_FE) addToMap(oduTypeMap, item.Tipe_ODU_FE, vendor, oduFe);
      if (item.Tipe_IDU_NE) addToMap(iduTypeMap, item.Tipe_IDU_NE, vendor, iduNe);
      if (item.Tipe_IDU_FE) addToMap(iduTypeMap, item.Tipe_IDU_FE, vendor, iduFe);
      if (item.Modem_IDU_NE) addToMap(modemTypeMap, item.Modem_IDU_NE, vendor, modemNe);
      if (item.Modem_IDU_FE) addToMap(modemTypeMap, item.Modem_IDU_FE, vendor, modemFe);
      if (item.Ukuran_Antenna_NE) addToMap(antennaSizeMap, item.Ukuran_Antenna_NE, vendor, antennaNe);
      if (item.Ukuran_Antenna_FE) addToMap(antennaSizeMap, item.Ukuran_Antenna_FE, vendor, antennaFe);
    });
    return {
      kpis: {
        totalOduNe,
        totalOduFe,
        totalIduNe,
        totalIduFe,
        totalModemNe,
        totalModemFe,
        totalAntennaNe,
        totalAntennaFe
      },
      chartData: {
        oduType: formatChart(oduTypeMap),
        iduType: formatChart(iduTypeMap),
        modemType: formatChart(modemTypeMap),
        antennaSize: formatChart(antennaSizeMap)
      }
    };
  }, [data]);
  if (error) {
    return <div className="text-red-400 p-6" data-id="element-629" data-mp="2ziup0hcotf24">Error: {error}</div>;
  }
  return <div className="space-y-6" data-id="element-630" data-mp="34iqwwroi9qmy">
      <h2 className="text-xl font-bold text-slate-100" data-id="element-631" data-mp="34js6erpvi52k">Equipment Analysis</h2>

      {/* KPI */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-8 gap-4" data-id="element-632" data-mp="2qk980qmv683g">
        {loading || !kpis ? Array(8).fill(0).map((_, i) => <CardSkeleton key={i} data-id="element-633" data-mp="3j7mzi97smbg6" data-mpi={i} />) : <>
            <KPICard title="ODU NE" value={kpis.totalOduNe.toLocaleString()} icon={ServerIcon} data-id="element-634" data-mp="3ja990ee8v111" />
            <KPICard title="ODU FE" value={kpis.totalOduFe.toLocaleString()} icon={ServerIcon} data-id="element-635" data-mp="39h0neozac328" />
            <KPICard title="IDU NE" value={kpis.totalIduNe.toLocaleString()} icon={CpuIcon} data-id="element-636" data-mp="61d5ybdjzgda" />
            <KPICard title="IDU FE" value={kpis.totalIduFe.toLocaleString()} icon={CpuIcon} data-id="element-637" data-mp="at4vcikz3g1u" />
            <KPICard title="Modem NE" value={kpis.totalModemNe.toLocaleString()} icon={WifiIcon} data-id="element-638" data-mp="klpl5tekr73g" />
            <KPICard title="Modem FE" value={kpis.totalModemFe.toLocaleString()} icon={WifiIcon} data-id="element-639" data-mp="aokylsgth9m4" />
            <KPICard title="Antenna NE" value={kpis.totalAntennaNe.toLocaleString()} icon={RadioIcon} data-id="element-640" data-mp="1p7oqyos6j0v" />
            <KPICard title="Antenna FE" value={kpis.totalAntennaFe.toLocaleString()} icon={RadioIcon} data-id="element-641" data-mp="3t2arf2ftnm2v" />
          </>}
      </div>

      {/* CHART */}
      {loading || !chartData ? <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6" data-id="element-642" data-mp="3e6cyru53wzve">
          {Array(4).fill(0).map((_, i) => <ChartSkeleton key={i} data-id="element-643" data-mp="3a5j9oxfxhf0x" data-mpi={i} />)}
        </div> : <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6" data-id="element-644" data-mp="1plxdeef3l4vg">
          {['oduType', 'iduType', 'modemType', 'antennaSize'].map((key, i) => <motion.div key={i} className="glass-panel p-6 rounded-xl h-[420px] flex flex-col" data-id="element-645" data-mp="1ynnd07j4va4b" data-mpi={i}>
              <h3 className="text-sm font-semibold text-slate-100 mb-4 uppercase" data-id="element-646" data-mp="23loy0y04djif">
                {key.replace('Type', ' Type')}
              </h3>

              <ResponsiveContainer data-id="element-647" data-mp="15x524kc644gc">
                <BarChart data={chartData[key] || []} layout="vertical" data-id="element-648" data-mp="1axb00i916n6v">
                  <CartesianGrid stroke="#334155" strokeDasharray="3 3" data-id="element-649" data-mp="1fxfgi89xvhx3" />
                  <XAxis type="number" stroke="#cbd5f5" data-id="element-650" data-mp="1fv2qr8xxw39h" />
                  <YAxis dataKey="name" type="category" stroke="#cbd5f5" width={110} data-id="element-651" data-mp="1koxgat087cgh" />

                  <Tooltip content={<CustomTooltip data-id="element-653" data-mp="s6jj8vmpruig" />} data-id="element-652" data-mp="s6t8ggzxpxn3" />
                  <Legend data-id="element-654" data-mp="s5i7eoxvj3zo" />

                  {Object.keys(VENDOR_COLORS).map(vendor => <Bar key={vendor} dataKey={vendor} stackId="a" fill={VENDOR_COLORS[vendor]} stroke={vendor === 'EID' ? '#64748b' : 'none'} strokeWidth={vendor === 'EID' ? 2 : 0} data-id="element-655" data-mp="3hrr3f49lw0d8" />)}
                </BarChart>
              </ResponsiveContainer>
            </motion.div>)}
        </div>}
    </div>;
}